/**
 * Optional utm_source, utm_medium, and utm_campaign capture for attribution.
 *
 * A present query value is stored in its own first-party cookie for 90 days.
 * A missing value leaves that cookie unchanged. Only utm_source falls back to
 * the current page path at signup. Medium and campaign have no path fallback.
 */

const COOKIE_NAME = "nrs_utm_source";
const MAX_AGE_SEC = 90 * 24 * 60 * 60;

function readCookie(name) {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  if (!m) return null;
  try {
    return decodeURIComponent(m[1]) || null;
  } catch {
    return null;
  }
}

const UTM_FIELDS = [
  ["utm_source", "nrs_utm_source"],
  ["utm_medium", "nrs_utm_medium"],
  ["utm_campaign", "nrs_utm_campaign"],
];

function writeUtmCookie(name, value) {
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${MAX_AGE_SEC}; Path=/; SameSite=Lax`;
}

/** Store whichever of source, medium, and campaign the current URL actually contains. */
export function captureUtmFieldsFromUrl() {
  if (typeof window === "undefined") return;
  const sp = new URLSearchParams(window.location.search);
  for (const [param, cookieName] of UTM_FIELDS) {
    const label = sp.get(param)?.trim();
    if (!label || label.length > 100) continue;
    writeUtmCookie(cookieName, label);
  }
}

export function captureUtmSourceFromUrl() {
  captureUtmFieldsFromUrl();
}

/**
 * Returns the utm_source stored from a previous URL param capture, or null.
 */
export function getStoredUtmSource() {
  return readCookie(COOKIE_NAME);
}

export function getStoredUtmMedium() {
  return readCookie("nrs_utm_medium");
}

export function getStoredUtmCampaign() {
  return readCookie("nrs_utm_campaign");
}

/**
 * Returns the utm_source to use at auth initiation time:
 * - Stored cookie value (from URL utm_source on any prior page visit), or
 * - Current page pathname as a fallback so every registration is attributed
 *   to the portal page that triggered it.
 */
export function getEffectiveUtmSource() {
  if (typeof window === "undefined") return null;
  return getStoredUtmSource() || window.location.pathname || null;
}
